import { RequestConfig } from '@umijs/max';
import './global.less';
import React from 'react';
import { Button, Input, message } from 'antd';
import { LogoutOutlined, SearchOutlined } from '@ant-design/icons';
import { requestConfig, clearAuth, getToken, getCurrentUser } from '@/utils/auth';
import { adminLogout } from '@/services/auth';
import { fm } from '@/utils/i18nText';
// 780CY-54K-55u 企业身份切换器（权限 = f(账号, 当前企业上下文)）
import CompanySwitcher from '@/components/CompanySwitcher';
import AccessDeniedHint from '@/components/AccessDeniedHint';

export const request = requestConfig;

/**
 * #746 角色/权限缓存 —— 必须【绑定 uid】
 *
 * ⛔ 血泪教训：以前把 roles 裸数组存进 localStorage，接口失败时无条件回退读取。
 *    换账号登录后（超级管理员 -> 企庭客服），只要 admin-permissions 慢一次，
 *    新账号就沿用了上一个账号的角色 —— 客服直接变成「超级管理员」，
 *    access.ts 的 isSuperAdmin 成立 = 越权拿到全部菜单。
 *
 * 所以：缓存里记 uid，读的时候 uid 不一致一律丢弃，退化为【最小权限】。
 * 宁可暂时没权限（刷新即可恢复），也绝不能把别人的权限给到当前账号。
 */
const ROLE_CACHE_KEY = 'admin_role_cache_v2';

function writeRoleCache(uid: any, roles: string[], permissions: string[]) {
  try {
    localStorage.setItem(
      ROLE_CACHE_KEY,
      JSON.stringify({ uid: Number(uid) || 0, roles, permissions, ts: Date.now() }),
    );
  } catch {}
}

function readRoleCache(uid: any): { roles: string[]; permissions: string[] } | null {
  try {
    const raw = localStorage.getItem(ROLE_CACHE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    // uid 不一致 = 这是【别人】的缓存，绝不能给当前账号
    if (!o || Number(o.uid) !== Number(uid)) return null;
    return {
      roles: Array.isArray(o.roles) ? o.roles : [],
      permissions: Array.isArray(o.permissions) ? o.permissions : [],
    };
  } catch {
    return null;
  }
}

/** 换账号时把旧格式的裸数组缓存清掉，避免残留被别处读到 */
function purgeLegacyRoleCache() {
  try {
    localStorage.removeItem('admin_roles');
    localStorage.removeItem('admin_permissions');
  } catch {}
}

export async function getInitialState(): Promise<{
  currentUser?: API.CurrentUser;
  name?: string;
  avatar?: string;
  permissions?: string[];
  roles?: string[];
}> {
  // 走 utils/auth 的统一身份解析（cookie .chpen.com/admin_auth 优先，localStorage 兜底），
  // 不再直接读 localStorage，保证从 APP/官网跳转过来即为已登录状态。
  // 每次启动都重新解析，不使用任何模块级缓存，避免复用上一次的身份。
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) return {};

    const token = getToken();
    if (!token) return { currentUser, name: currentUser?.name, avatar: currentUser?.avatar };

    // 同步获取权限（await 确保渲染前有权限数据）
    let permissions: string[] = [];
    let roles: string[] = [];

    try {
      const ctrl = new AbortController();
      const timeoutId = setTimeout(() => ctrl.abort(), 5000);
      const res = await fetch('https://api.chpen.com/api/backend/v1/admin-permissions', {
        method: 'GET',
        signal: ctrl.signal,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (data?.code === 0 && data?.data?.roles) {
        const allPerms: string[] = [];
        const roleNames: string[] = [];
        data.data.roles.forEach((role: any) => {
          roleNames.push(role.name);
          if (role.permissions) {
            role.permissions.forEach((p: any) => {
              if (!allPerms.includes(p.name)) allPerms.push(p.name);
            });
          }
        });
        permissions = allPerms;
        roles = roleNames;
        // #746 绑定当前 uid，并清掉旧格式的裸数组缓存
        writeRoleCache(currentUser?.id, roleNames, allPerms);
        purgeLegacyRoleCache();
      } else {
        // ⛔ 铁律「前端轮询非 ok 必须写 else」：接口返回 code≠0 时不能静默变空权限。
        //   1403 = 未授权/登录失效 -> 绝不用旧缓存冒充（最小权限），走空；
        //   其余错误（超时/5xx/网络异常）-> 读同一 uid 的缓存，保证 loading 消失、不白屏。
        if (data?.code !== 1403) {
          const cached = readRoleCache(currentUser?.id);
          if (cached) {
            permissions = cached.permissions;
            roles = cached.roles;
          }
        }
      }
    } catch {
      // #746 权限获取失败：只读【同一个 uid】的缓存，uid 不一致一律丢弃（最小权限）
      const cached = readRoleCache(currentUser?.id);
      if (cached) {
        permissions = cached.permissions;
        roles = cached.roles;
      }
    }

    return { currentUser, name: currentUser?.name, avatar: currentUser?.avatar, permissions, roles };
  } catch (e) {
    console.warn("getInitialState error:", e);
    // Fallback: 返回最小可用 state，保证 loading 一定消失，不白屏
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) return {};
      // #746 同样只认同一个 uid 的缓存
      const cached = readRoleCache(currentUser?.id);
      return {
        currentUser,
        name: currentUser.name,
        permissions: cached ? cached.permissions : [],
        roles: cached ? cached.roles : [],
      };
    } catch (f) {
      return {};
    }
  }
}

export const layout = ({
  initialState,
}: {
  initialState: {
    currentUser?: API.CurrentUser;
    name?: string;
    avatar?: string;
    permissions?: string[];
    roles?: string[];
  };
}) => {
  return {
    // 780CY-54K-99 · 无权限兜底：切到自然人后菜单全空，这里说清原因并给切回入口
    unAccessible: React.createElement(AccessDeniedHint),
    title: fm('chpen.com 管理后台'),
    logo: '/logo.svg',
    menu: { locale: false },
    avatarProps: {
      src: initialState?.avatar || initialState?.currentUser?.avatar,
      title: initialState?.name || initialState?.currentUser?.name || fm('管理员'),
      size: 'small' as const,
    },
    fixedHeader: true,
    fixSiderbar: true,
    onMenuHeaderClick: () => window.open('https://www.chpen.com', '_blank'),
    menuFooterRender: undefined,
    rightContentRender: () => {
      // #780-K 绑定抖音：拿管理员授权地址后整页跳抖音，回调页回写 tx_admins.dy_open_id
      const bindDouyin = React.createElement(Button, {
        type: 'text',
        className: 'chpen-bind-douyin-btn',
        size: 'small',
        onClick: async () => {
          try {
            const token = getToken();
            const r = await fetch('https://api.chpen.com/api/auth/v1/douyin/admin-bind-authorize?nocache=1', {
              headers: token ? { Authorization: 'Bearer ' + token } : {},
            });
            const j = await r.json();
            const url = j && j.data && j.data.url;
            if (j && j.code === 0 && url) {
              window.location.href = url;
              return;
            }
            message.warning(fm('抖音绑定暂不可用'));
          } catch (e) {
            message.warning(fm('抖音绑定暂不可用'));
          }
        },
        style: { fontSize: 13 },
      }, fm('绑定抖音'));
      const logout = React.createElement(Button, {
        type: 'text',
        className: 'chpen-logout-btn',
        size: 'small',
        icon: React.createElement(LogoutOutlined),
        onClick: async () => {
          try {
            await adminLogout();
          } catch {
            // 即使 API 调用失败也继续清除本地登录状态
          }
          clearAuth();
          message.success(fm('已退出登录'));
          window.location.href = '/user/login';
        },
        style: { fontSize: 13 },
      }, fm('退出登录'));
      return React.createElement(
        React.Fragment,
        null,
        React.createElement(CompanySwitcher),
        bindDouyin,
        logout,
      );
    },
  };
};
